import * as type from 'store/actions/types';

// A query change (filters or sort) drops that tab's cached rows so it shows a
// skeleton instead of the previous query's data while the refetch is in
// flight. Only applied when the query actually changed: an identical dispatch
// (re-applying the same filter, clearing already-empty filters) triggers no
// refetch, so dropping rows then would leave the tab empty.
var DROP_HISTORY_ROWS = { records: null, recordsToken: null, rowCount: null };
var DROP_SCHEDULED_ROWS = {
  scheduledRecords: null,
  scheduledToken: null,
  scheduledRowCount: null,
};
function sameFilters(a, b) {
  return JSON.stringify(a || {}) === JSON.stringify(b || {});
}

var initialState = {
  // Two server-provided pivots of the same inspection hierarchy from GET /inspections.
  // byAssetType items: { kind: 'assetTypeNode', assetTypeFieldName, assetTypeFieldPathHash, assetTypeValue, inspections: [ { inspectionUUID, recordsFieldTitle } ] }
  //                     or { kind: 'inspection', inspectionUUID, recordsFieldTitle } (ungrouped)
  // byInspection items: { kind: 'inspection', inspectionUUID, recordsFieldTitle, assetTypeFieldName, assetTypeFieldPathHash, assetTypes: [ { assetTypeValue } ] }
  //                     or { kind: 'inspection', inspectionUUID, recordsFieldTitle } (no asset-type field)
  inspectionsByAssetType: [],
  inspectionsByInspection: [],
  pivot: 'byInspection', // active dropdown pivot: 'byInspection' | 'byAssetType' (persisted to sessionStorage)
  currentInspection: null,
  records: null,
  recordsToken: null,
  recordsColumns: null,
  recordsCustomColumns: [],
  rowCount: null,
  inspectionSettings: null,
  inspectionSettingsRaw: null,
  scheduledRecords: null,
  scheduledColumns: null,
  scheduledCustomColumns: [],
  scheduledToken: null,
  scheduledRowCount: null,
  scheduledColumnFilters: {},
  scheduledSortColumnKey: null,
  scheduledSortDirection: null,
  selectionMode: 'all',
  // 'all' -> null. 'inspection' -> inspectionUUID (string).
  // 'inspectionType' -> { inspectionUUID, assetTypeValue }.
  // 'assetType' -> { hash, value } (assetTypeFieldPathHash, assetTypeValue).
  selectionValue: null,
  columnFilters: {},
  // Universal time-range selector (toolbar). `from`/`to` are resolved moments
  // (or null) used both for the selector UI and to derive the unix-second
  // timestamp bounds sent to the history API.
  dateRange: {
    from: null,
    to: null,
    past: 'all',
    future: 'all',
    customFrom: null,
    customTo: null,
  },
  sortColumnKey: null,
  sortDirection: null,
  performance: null, // { passed, failed, corrected, incomplete, total, overdue, upcoming, neglected, passRateByInspection } from /inspections/performance; null when unloaded
};

// Resolve an inspection by uuid within a single pivot list; returns { uuid, name }.
function findInspectionInList(list, uuid) {
  for (var i = 0; i < list.length; i++) {
    var item = list[i];
    if (item.kind === 'inspection' && item.inspectionUUID === uuid) {
      return { uuid: item.inspectionUUID, name: item.recordsFieldTitle };
    }
    if (item.kind === 'assetTypeNode' && item.inspections) {
      for (var j = 0; j < item.inspections.length; j++) {
        var ins = item.inspections[j];
        if (ins.inspectionUUID === uuid) {
          return { uuid: ins.inspectionUUID, name: ins.recordsFieldTitle };
        }
      }
    }
  }
  return null;
}

// Resolve an inspection by uuid from either pivot (byInspection preferred).
function findInspectionByUUID(byInspection, byAssetType, uuid) {
  return (
    findInspectionInList(byInspection || [], uuid) ||
    findInspectionInList(byAssetType || [], uuid)
  );
}

var inspect = function (state, action) {
  if (state === undefined) state = initialState;

  switch (action.type) {
    case type.SET_INSPECTIONS: {
      var byAssetType = action.payload.byAssetType || [];
      var byInspection = action.payload.byInspection || [];
      var newState = Object.assign({}, state, {
        inspectionsByAssetType: byAssetType,
        inspectionsByInspection: byInspection,
      });
      // If current inspection is set, re-resolve it from the fresh data
      if (state.currentInspection) {
        var refreshed = findInspectionByUUID(
          byInspection,
          byAssetType,
          state.currentInspection.uuid,
        );
        if (refreshed) {
          newState.currentInspection = refreshed;
        }
      }
      return newState;
    }

    case type.SET_INSPECT_PIVOT: {
      return Object.assign({}, state, {
        pivot: action.payload.pivot,
      });
    }

    case type.SET_CURRENT_INSPECTION: {
      var resolved = findInspectionByUUID(
        state.inspectionsByInspection,
        state.inspectionsByAssetType,
        action.payload.uuid,
      );
      var currentPatch = {
        currentInspection: resolved || state.currentInspection,
      };
      // An inspectionType selection is inspection-like but carries a type-scoped
      // mode/value that SET_INSPECT_SELECTION already set; only the resolved name
      // matters here, so leave mode/value untouched in that case.
      if (state.selectionMode !== 'inspectionType') {
        currentPatch.selectionMode = 'inspection';
        currentPatch.selectionValue = resolved
          ? resolved.uuid
          : state.selectionValue;
      }
      return Object.assign({}, state, currentPatch);
    }

    case type.SET_INSPECTION_RECORDS: {
      var existingRecords = state.records || [];
      var newRecords = action.payload.isNewSet
        ? action.payload.records
        : existingRecords.concat(action.payload.records);
      var nextState = {
        records: newRecords,
        recordsToken: action.payload.token || null,
        rowCount:
          action.payload.rowCount !== undefined
            ? action.payload.rowCount
            : state.rowCount,
      };
      if (action.payload.columns !== undefined) {
        nextState.recordsColumns = action.payload.columns;
      }
      if (Array.isArray(action.payload.customColumns)) {
        nextState.recordsCustomColumns = action.payload.customColumns;
      }
      return Object.assign({}, state, nextState);
    }

    // Optimistic patch of one History row after a write (void, deficiency
    // action), keyed by the entry's slateUUID. Server truth replaces it on the
    // delayed refetch (see INSPECT_REINDEX_REFRESH_DELAY_MS).
    case type.PATCH_INSPECTION_HISTORY_RECORD: {
      if (!state.records) return state;
      var patchSlateUUID = action.payload.slateUUID;
      var patchChanges = action.payload.changes;
      var patched = false;
      var patchedRecords = state.records.map(function (record) {
        if (!record || record.slateUUID !== patchSlateUUID) return record;
        patched = true;
        return Object.assign({}, record, patchChanges);
      });
      if (!patched) return state;
      return Object.assign({}, state, { records: patchedRecords });
    }

    case type.CLEAR_INSPECTION_RECORDS: {
      return Object.assign({}, state, {
        records: null,
        recordsToken: null,
        rowCount: null,
      });
    }

    case type.SET_INSPECTION_SETTINGS: {
      var raw = action.payload.rawSettings;
      var uuid = action.payload.inspectionUUID || (raw && raw.uuid);
      var title = raw && raw.inspectionRecordsFieldTitle;
      var newState = Object.assign({}, state, {
        inspectionSettings: action.payload.settings,
        inspectionSettingsRaw:
          action.payload.rawSettings !== undefined
            ? action.payload.rawSettings
            : state.inspectionSettingsRaw,
      });
      // Ensure currentInspection is populated for this inspection so consumers
      // (e.g. breadcrumbs) can resolve its name on refresh when the inspections
      // data hasn't been fetched yet.
      if (uuid && title) {
        if (!state.currentInspection || state.currentInspection.uuid !== uuid) {
          newState.currentInspection = { uuid: uuid, name: title };
        }
      }
      return newState;
    }

    case type.CLEAR_INSPECTION_SETTINGS: {
      return Object.assign({}, state, {
        inspectionSettings: null,
        inspectionSettingsRaw: null,
      });
    }

    case type.SET_INSPECTION_SCHEDULED: {
      var existingScheduledRecords = state.scheduledRecords || [];
      var newScheduledRecords = action.payload.isNewSet
        ? action.payload.records
        : existingScheduledRecords.concat(action.payload.records);
      var nextScheduledState = {
        scheduledRecords: newScheduledRecords,
        scheduledToken: action.payload.token || null,
        scheduledRowCount:
          action.payload.rowCount !== undefined
            ? action.payload.rowCount
            : state.scheduledRowCount,
      };
      if (action.payload.columns !== undefined) {
        nextScheduledState.scheduledColumns = action.payload.columns;
      }
      if (Array.isArray(action.payload.customColumns)) {
        nextScheduledState.scheduledCustomColumns = action.payload.customColumns;
      }
      return Object.assign({}, state, nextScheduledState);
    }

    case type.CLEAR_INSPECTION_SCHEDULED: {
      return Object.assign({}, state, {
        scheduledRecords: null,
        scheduledToken: null,
        scheduledRowCount: null,
      });
    }

    case type.SET_INSPECT_SCHEDULED_COLUMN_FILTER: {
      var nextScheduledFilters = Object.assign(
        {},
        state.scheduledColumnFilters,
      );
      var scheduledKey = action.payload.columnKey;
      var scheduledValue = action.payload.value;
      // Empty array = cleared multi-select picker; treat like an empty string.
      var scheduledCleared =
        scheduledValue === '' ||
        scheduledValue === null ||
        scheduledValue === undefined ||
        (Array.isArray(scheduledValue) && scheduledValue.length === 0);
      if (scheduledCleared) {
        delete nextScheduledFilters[scheduledKey];
      } else {
        nextScheduledFilters[scheduledKey] = scheduledValue;
      }
      return Object.assign(
        {},
        state,
        { scheduledColumnFilters: nextScheduledFilters, scheduledToken: null },
        sameFilters(nextScheduledFilters, state.scheduledColumnFilters)
          ? null
          : DROP_SCHEDULED_ROWS,
      );
    }

    case type.CLEAR_INSPECT_SCHEDULED_COLUMN_FILTERS: {
      return Object.assign(
        {},
        state,
        { scheduledColumnFilters: {}, scheduledToken: null },
        sameFilters({}, state.scheduledColumnFilters)
          ? null
          : DROP_SCHEDULED_ROWS,
      );
    }

    case type.SET_INSPECT_SCHEDULED_SORT: {
      var scheduledDirection = action.payload.direction;
      var scheduledColumnKey =
        scheduledDirection === null ? null : action.payload.columnKey;
      var scheduledSortChanged =
        scheduledColumnKey !== state.scheduledSortColumnKey ||
        scheduledDirection !== state.scheduledSortDirection;
      return Object.assign(
        {},
        state,
        {
          scheduledSortColumnKey: scheduledColumnKey,
          scheduledSortDirection: scheduledDirection,
          scheduledToken: null,
        },
        scheduledSortChanged ? DROP_SCHEDULED_ROWS : null,
      );
    }

    case type.SET_INSPECT_SCHEDULED_COLUMNS: {
      return Object.assign({}, state, {
        scheduledColumns:
          action.payload.columns !== undefined ? action.payload.columns : null,
        scheduledCustomColumns: Array.isArray(action.payload.customColumns)
          ? action.payload.customColumns
          : state.scheduledCustomColumns,
      });
    }

    case type.SET_INSPECT_COLUMN_FILTER: {
      var nextFilters = Object.assign({}, state.columnFilters);
      var key = action.payload.columnKey;
      var value = action.payload.value;
      // Empty array = cleared multi-select picker; treat like an empty string.
      var cleared =
        value === '' ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0);
      if (cleared) {
        delete nextFilters[key];
      } else {
        nextFilters[key] = value;
      }
      return Object.assign(
        {},
        state,
        { columnFilters: nextFilters, recordsToken: null },
        sameFilters(nextFilters, state.columnFilters) ? null : DROP_HISTORY_ROWS,
      );
    }

    case type.SET_INSPECT_COLUMNS: {
      return Object.assign({}, state, {
        recordsColumns:
          action.payload.columns !== undefined ? action.payload.columns : null,
        recordsCustomColumns: Array.isArray(action.payload.customColumns)
          ? action.payload.customColumns
          : state.recordsCustomColumns,
      });
    }

    case type.CLEAR_INSPECT_COLUMN_FILTERS: {
      return Object.assign(
        {},
        state,
        { columnFilters: {}, recordsToken: null },
        sameFilters({}, state.columnFilters) ? null : DROP_HISTORY_ROWS,
      );
    }

    case type.SET_INSPECT_DATE_RANGE: {
      // The range is shared, but each tab scopes to one side of it: History keys
      // on the past side, Scheduled on the future side, Performance on both (see
      // pastRangeKey / futureRangeKey / performanceFetchKey in Inspect.js). Clear
      // only the data whose side actually changed -- marking it dirty so it shows
      // the immediate cold-load skeleton -- and leave the unaffected side's data
      // in place. A blanket null would blank the unaffected tab: its fetch key
      // would be unchanged, so _maybeFetch* skips the refetch and the nulled rows
      // never reload. Custom bounds drive both sides.
      var nextRange = action.payload.dateRange || {};
      var prevRange = state.dateRange || {};
      var customChanged =
        (prevRange.customFrom || null) !== (nextRange.customFrom || null) ||
        (prevRange.customTo || null) !== (nextRange.customTo || null);
      var pastChanged =
        (prevRange.past || 'all') !== (nextRange.past || 'all') ||
        customChanged;
      var futureChanged =
        (prevRange.future || 'all') !== (nextRange.future || 'all') ||
        customChanged;
      var dateRangePatch = { dateRange: action.payload.dateRange };
      if (pastChanged) {
        dateRangePatch.records = null;
        dateRangePatch.recordsToken = null;
      }
      if (futureChanged) {
        dateRangePatch.scheduledRecords = null;
        dateRangePatch.scheduledToken = null;
      }
      if (pastChanged || futureChanged) {
        dateRangePatch.performance = null;
      }
      return Object.assign({}, state, dateRangePatch);
    }

    case type.SET_INSPECT_SORT: {
      var directionPayload = action.payload.direction;
      var keyPayload =
        directionPayload === null ? null : action.payload.columnKey;
      var sortChanged =
        keyPayload !== state.sortColumnKey ||
        directionPayload !== state.sortDirection;
      return Object.assign(
        {},
        state,
        {
          sortColumnKey: keyPayload,
          sortDirection: directionPayload,
          recordsToken: null,
        },
        sortChanged ? DROP_HISTORY_ROWS : null,
      );
    }

    case type.SET_INSPECT_SELECTION: {
      // An inspectionType selection is a single inspection scoped to one asset-type
      // value, so it is inspection-like: keep currentInspection / inspectionSettings.
      // Only 'all' and 'assetType' clear them.
      var selectionMode = action.payload.mode;
      var isInspectionLike =
        selectionMode === 'inspection' || selectionMode === 'inspectionType';
      return Object.assign({}, state, {
        selectionMode: selectionMode,
        selectionValue: action.payload.value,
        currentInspection: isInspectionLike ? state.currentInspection : null,
        inspectionSettings: isInspectionLike ? state.inspectionSettings : null,
        inspectionSettingsRaw: isInspectionLike
          ? state.inspectionSettingsRaw
          : null,
        columnFilters: {},
        sortColumnKey: null,
        sortDirection: null,
        recordsToken: null,
        scheduledColumnFilters: {},
        scheduledSortColumnKey: null,
        scheduledSortDirection: null,
        scheduledToken: null,
        performance: null,
      });
    }

    case type.SET_INSPECTION_PERFORMANCE: {
      return Object.assign({}, state, {
        performance: action.payload || null,
      });
    }

    case type.CLEAR_INSPECTION_PERFORMANCE: {
      return Object.assign({}, state, {
        performance: null,
      });
    }

    case type.CLEAR_INSPECT: {
      return Object.assign({}, initialState);
    }

    case type.CLEAR_SLATE_VIEWS: {
      return Object.assign({}, initialState);
    }

    default:
      return state;
  }
};

export default inspect;
